/**
 * Single source of truth for client-side password validation.
 *
 * why: Mirrors the ASP.NET Identity policy enforced by the backend in
 * FacilityFlow.Api/src/FacilityFlow.Core/Modules/Employees/EmployeeModuleExtensions.cs
 * (RequiredLength = 8, RequireDigit/Uppercase/Lowercase = true,
 * RequireNonAlphanumeric = false). Keeping the rules here lets every password
 * form (create employee, admin reset, profile change, auth reset) and the
 * PasswordCriteria display share the exact same logic — no duplicated regex.
 */

export interface PasswordRule {
  /** Maps 1:1 to the i18n key under `validation:password.<key>`. */
  key: 'minLength' | 'uppercase' | 'lowercase' | 'digit';
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  { key: 'minLength', test: (pw) => pw.length >= 8 },
  { key: 'uppercase', test: (pw) => /[A-Z]/.test(pw) },
  { key: 'lowercase', test: (pw) => /[a-z]/.test(pw) },
  { key: 'digit', test: (pw) => /\d/.test(pw) },
];

/** True only when the password satisfies every rule of the backend policy. */
export function isPasswordValid(password: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(password));
}
